import type { AppProps } from "next/app";
import App, { AppContext } from 'next/app';
import { loadMessages } from '@/utils/i18n';
import { Analytics } from "@vercel/analytics/react"
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Script from 'next/script';
import { useEffect } from 'react';
import "@/dist/styles.css";
import Layout from '@/templates/Layout';
import { useRouter } from 'next/router';
import { CookieConsentProvider, useCookieConsent } from "@/components/hooks/useCookieConsent";
import CookieConsent from "@/components/CookieConsent";
import PromoBanner from "@/components/PromoBanner";
import OnboardingCoach from "@/components/OnboardingCoach";
import { NextIntlClientProvider } from 'next-intl';
import { UserContextProvider as PublicUserContextProvider } from '@/utils/useUser';

const META_PIXEL_ID = '1575605800639328';

function ConsentTracking({ gaId, enableMetaPixel }: { gaId: string; enableMetaPixel: boolean }) {
    const { isConsentGiven } = useCookieConsent();
    const router = useRouter();

    useEffect(() => {
        if (!isConsentGiven) return;

        const trackPageView = (url: string) => {
            const trackingWindow = window as typeof window & {
                gtag?: (...args: any[]) => void;
                fbq?: (...args: any[]) => void;
            };

            trackingWindow.gtag?.('config', gaId, { page_path: url });
            if (enableMetaPixel) {
                trackingWindow.fbq?.('track', 'PageView');
            }
        };

        router.events.on('routeChangeComplete', trackPageView);
        return () => router.events.off('routeChangeComplete', trackPageView);
    }, [enableMetaPixel, gaId, isConsentGiven, router.events]);

    if (isConsentGiven !== true) return null;

    return (
        <>
            <Script
                strategy="lazyOnload"
                src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
            />
            <Script
                id="google-analytics"
                strategy="lazyOnload"
                dangerouslySetInnerHTML={{
                    __html: `
                        window.dataLayer = window.dataLayer || [];
                        function gtag(){dataLayer.push(arguments);}
                        gtag('js', new Date());
                        gtag('config', '${gaId}');
                    `,
                }}
            />
            {enableMetaPixel && (
                <>
                    <Script
                        id="meta-pixel"
                        strategy="afterInteractive"
                        dangerouslySetInnerHTML={{
                            __html: `
                                !function(f,b,e,v,n,t,s)
                                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                                n.queue=[];t=b.createElement(e);t.async=!0;
                                t.src=v;s=b.getElementsByTagName(e)[0];
                                s.parentNode.insertBefore(t,s)}(window, document,'script',
                                'https://connect.facebook.net/en_US/fbevents.js');
                                fbq('init', '${META_PIXEL_ID}');
                                fbq('track', 'PageView');
                            `,
                        }}
                    />
                </>
            )}
        </>
    );
}

export default function MyApp({ Component, pageProps }: AppProps<any>) {
    const { messages, ...restPageProps } = pageProps;
    const gaId = process.env.NEXT_PUBLIC_GA_ID || 'G-0V1FRFNWE8';
    const UserContextProvider = dynamic(() =>
        import("@/utils/useUser").then((module) => module.UserContextProvider)
    );
    const CompanyContextProvider = dynamic(() =>
        import("@/utils/CompanyContext").then((module) => module.CompanyContextProvider)
    );
    const CampaignContextProvider = dynamic(() =>
        import("@/utils/CampaignContext").then((module) => module.CampaignContextProvider)
    );
    const AffiliateContextProvider = dynamic(() =>
        import("@/utils/AffiliateContext").then((module) => module.AffiliateContextProvider)
    );
    const NotificationsContextProvider = dynamic(() =>
        import("@/utils/NotificationsContext").then((module) => module.NotificationsContextProvider)
    );
    const router = useRouter();

    useEffect(() => {
        document.body.classList?.remove('loading');

        if (router?.asPath?.indexOf("&token_type=bearer&type=recovery") > 0) {
            let access_token = router?.asPath?.split("access_token=")[1].split("&")[0];
            router.push('/reset-password?passwordReset=true&access_token=' + access_token + '');
        }
    });

    useEffect(() => {
        const p = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
        const embedded = p?.get('embedded');
        if (embedded !== '1' && embedded !== 'true') return;

        const metaApiKey =
            typeof document !== 'undefined'
                ? document.querySelector('meta[name="shopify-api-key"]')?.getAttribute('content')
                : null;
        const appBridgeScriptPresent =
            typeof document !== 'undefined'
                ? !!document.querySelector('script[src*="shopifycloud/app-bridge.js"]')
                : false;
    }, [router?.asPath]);

    useEffect(() => {
        let attempts = 0;
        const maxAttempts = 24;
        const hideWidget = router.pathname.startsWith('/dashboard');
        document.body.classList.toggle('smartsupp-hidden', hideWidget);
        const syncChatVisibility = () => {
            const supportWindow = window as typeof window & {
                smartsupp?: (...args: any[]) => void;
            };

            if (typeof supportWindow.smartsupp !== 'function') return false;

            supportWindow.smartsupp(
                router.pathname.startsWith('/dashboard') ? 'chat:hide' : 'chat:show'
            );
            return true;
        };

        if (syncChatVisibility()) return;

        const intervalId = window.setInterval(() => {
            attempts += 1;
            if (syncChatVisibility() || attempts >= maxAttempts) {
                window.clearInterval(intervalId);
            }
        }, 250);

        return () => window.clearInterval(intervalId);
    }, [router.pathname]);

    return (
        <NextIntlClientProvider
            locale={router.locale}
            messages={messages}
            // ✅ щоб не падало, якщо на якійсь сторінці ще нема messages
            // (але краще потім додати getStaticProps всюди)
            // @ts-ignore
            timeZone="Europe/Warsaw"
        >
            <>
                <Analytics />
                <style jsx global>{`
                    body.smartsupp-hidden [id*='smartsupp'],
                    body.smartsupp-hidden [class*='smartsupp'],
                    body.smartsupp-hidden iframe[src*='smartsuppchat.com'] {
                        display: none !important;
                    }
                `}</style>
                <Script
                    id="smartsupp"
                    strategy="afterInteractive"
                    dangerouslySetInnerHTML={{
                        __html: `
        var _smartsupp = _smartsupp || {};
        _smartsupp.key = '42fe8e340e559fd5f7a03db1f4daf5d5b90fc2c6';
        _smartsupp.hideWidget = ${router.pathname.startsWith('/dashboard') ? 'true' : 'false'};

        var existingQueue = window.smartsupp;
        if (typeof existingQueue !== 'function') {
          var o = window.smartsupp = function() {
            o._.push(arguments);
          };
          o._ = Array.isArray(existingQueue && existingQueue._) ? existingQueue._ : [];
        }

        var existingLoader = document.getElementById('smartsupp-loader');
        if (!existingLoader) {
          var c = document.createElement('script');
          c.id = 'smartsupp-loader';
          c.type = 'text/javascript';
          c.charset = 'utf-8';
          c.async = true;
          c.src = 'https://www.smartsuppchat.com/loader.js?';
          document.head.appendChild(c);
        }
      `,
                    }}
                />
                {
                    typeof window !== "undefined" && window.location.href.includes('/dashboard/') &&
                    <Head>
                        <script
                            dangerouslySetInnerHTML={{
                                __html: `!function(w,d,i,s){function l(){if(!d.getElementById(i)){var f=d.getElementsByTagName(s)[0],e=d.createElement(s);e.type="text/javascript",e.async=!0,e.src="https://canny.io/sdk.js",f.parentNode.insertBefore(e,f)}}if("function"!=typeof w.Canny){var c=function(){c.q.push(arguments)};c.q=[],w.Canny=c,"complete"===d.readyState?l():w.attachEvent?w.attachEvent("onload",l):w.addEventListener("load",l,!1)}}(window,document,"canny-jssdk","script");`
                            }}
                        />
                    </Head>
                }
                {/* Умовний рендеринг SEOMeta тільки для не-блог сторінок */}
                {/*{!router.pathname.startsWith('/blog/') && <SEOMeta />}*/}
                <div>
                    {
                        router.pathname.indexOf('/dashboard') > -1 ?
                            <UserContextProvider>
                                <CompanyContextProvider>
                                    <CampaignContextProvider>
                                        <AffiliateContextProvider>
                                            <NotificationsContextProvider>
                                                <CookieConsentProvider>
                                                    <ConsentTracking
                                                        gaId={gaId}
                                                        enableMetaPixel={false}
                                                    />
                                                    <Layout>
                                                        <Component {...pageProps} />
                                                    </Layout>
                                                    <OnboardingCoach />
                                                    <CookieConsent />
                                                </CookieConsentProvider>
                                            </NotificationsContextProvider>
                                        </AffiliateContextProvider>
                                    </CampaignContextProvider>
                                </CompanyContextProvider>
                            </UserContextProvider>
                            :
                            <CookieConsentProvider>
                                <ConsentTracking
                                    gaId={gaId}
                                    enableMetaPixel={true}
                                />
                                <PublicUserContextProvider>
                                    <PromoBanner />
                                    <Layout>
                                        <Component {...pageProps} />
                                    </Layout>
                                    <CookieConsent />
                                </PublicUserContextProvider>
                            </CookieConsentProvider>
                    }
                </div>
            </>
        </NextIntlClientProvider>
    );
};

MyApp.getInitialProps = async (appContext: AppContext) => {
    const appProps = await App.getInitialProps(appContext);
    const existingMessages = (appProps.pageProps as any)?.messages;
    if (existingMessages) return appProps;

    const locale = appContext.ctx.locale || 'en';
    let messages: any;
    try {
        messages = await loadMessages(locale);
    } catch {
        messages = await loadMessages('en');
    }

    return {
        ...appProps,
        pageProps: {
            ...appProps.pageProps,
            messages
        }
    };
};
